import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { challengeAPI } from '@/lib/api';
import { Sparkles, ArrowLeft, Send, Loader2, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const DailyChallenge = () => {
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState(null);
  const [rewritten, setRewritten] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadChallenge();
  }, []);

  const loadChallenge = async () => {
    try {
      const data = await challengeAPI.getDaily();
      setChallenge(data);
      if (data.completed_at) {
        setResult({score: data.score, feedback: data.feedback});
        setRewritten(data.rewritten_prompt);
      }
    } catch (error) {
      console.error('Load error:', error);
    }
  };

  const handleSubmit = async () => {
    if (!rewritten.trim()) {
      toast.error('Please write a prompt first');
      return;
    }

    setIsSubmitting(true);
    try {
      const data = await challengeAPI.submit(challenge.challenge_id, rewritten);
      setResult(data);
      toast.success(`Challenge complete! Score: ${data.score}/100 (+${data.points_earned} pts)`);
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <nav className="px-6 py-4 bg-white/80 backdrop-blur-sm border-b-4 border-black sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Sparkles className="h-7 w-7 text-primary" />
          <h1 className="text-xl font-bold">Daily Challenge</h1>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <h2 className="text-5xl font-bold mb-4 text-center">Today's <span className="text-secondary">Challenge</span></h2>
        <p className="text-xl text-center mb-12 text-gray-700">Improve this prompt and get AI-powered feedback</p>

        {challenge ? (
          <div className="space-y-8">
            <Card className="p-8 bg-white border-4 border-black card-shadow">
              <h3 className="text-2xl font-bold mb-4">Original Prompt</h3>
              <div className="bg-gray-100 p-6 rounded-lg border-2 border-black font-mono">
                {challenge.original_prompt}
              </div>
            </Card>

            <Card className="p-8 bg-gradient-to-br from-secondary/10 to-pink-100 border-4 border-black card-shadow">
              <h3 className="text-2xl font-bold mb-4">Your Improved Version</h3>
              <Textarea 
                value={rewritten}
                onChange={(e) => setRewritten(e.target.value)}
                placeholder="Rewrite the prompt with more detail, better structure, and improved clarity..."
                className="min-h-[200px] border-2 border-black mb-6 font-mono"
                disabled={false}
                data-testid="rewritten-input"
              />
              <Button 
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="bg-secondary text-white rounded-lg w-full py-6 text-lg button-shadow font-bold"
                data-testid="submit-button"
              >
                {isSubmitting ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting...</>
                ) : result ? (
                  <><Send className="mr-2 h-5 w-5" /> Try Again</>
                ) : (
                  <><Send className="mr-2 h-5 w-5" /> Submit for Scoring</>
                )}
              </Button>
            </Card>

            {result && (
              <Card className="p-8 bg-gradient-to-br from-accent/20 to-yellow-100 border-4 border-black card-shadow" data-testid="result-card">
                <div className="flex items-center gap-4 mb-6">
                  <Trophy className="h-12 w-12 text-accent" />
                  <div>
                    <h3 className="text-3xl font-bold">Score: {result.score}/100</h3>
                    <p className="text-gray-600">+{result.points_earned} points earned!</p>
                  </div>
                </div>
                <div className="bg-white p-6 rounded-lg border-2 border-black">
                  <h4 className="font-bold mb-2">AI Feedback:</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">{result.feedback}</p>
                </div>
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="mt-6 w-full bg-primary text-white button-shadow font-bold"
                >
                  Back to Dashboard
                </Button>
              </Card>
            )}
          </div>
        ) : (
          <div className="flex justify-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyChallenge;
